import { useState } from "react";
import { Button, TextField, Grid, IconButton, CircularProgress, Stack } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Add, Delete } from "@mui/icons-material";
import GenericModal from "../../../components/buttons/genericModal";
import { backendPostWithoutRefresh } from "../../../helpers/callBackend";


const AddCustomTagButton = (props) => {
	const [visible, setVisible] = useState(false);
	const [inFlight, setInFlight] = useState(false);
	const [rows, setRows] = useState([{ name: "", description: "" }]); // At least one row by default

	const handleInputChange = (index, field, value) => {
		const updatedRows = [...rows];
		updatedRows[index][field] = value;
		setRows(updatedRows);
	};

	const handlePaste = (e, index) => {
		const pastedData = e.clipboardData.getData("text");
		const pastedRows = pastedData.split("\n").filter(Boolean);

		// If it's just a simple single-line paste, allow default behavior
		if (pastedRows.length === 1 && !pastedData.includes("\t")) {
			return;
		}

		e.preventDefault(); // Prevent default pasting into input

		const newRowData = pastedRows.map((row) => {
			const columns = row.split("\t");
			return {
				name: columns[0]?.trim() || "",
				description: columns[1]?.trim() || "",
			};
		});

		setRows((prevRows) => {
			const updated = [...prevRows];

			// Replace starting at the current index
			for (let i = 0; i < newRowData.length; i++) {
				const rowIndex = index + i;
				if (updated[rowIndex]) {
					updated[rowIndex] = newRowData[i];
				} else {
					updated.push(newRowData[i]);
				}
			}
			return updated;
		});
	};

	const addRow = () => {
		setRows([...rows, { name: "", description: "" }]);
	};

	const removeRow = (index) => {
		if (rows.length > 1) {
			setRows(rows.filter((_, i) => i !== index));
		}
	};

	const resetForm = () => {
		setRows([{ name: "", description: "" }]); // Reset to one empty row
	};

	const onFormSubmit = async () => {
		const validRows = rows.filter((row) => row.name.trim());

		if (validRows.length === 0) {
			console.warn("No valid rows to submit.");
			return;
		}

		setInFlight(true);
		try {
			for (let row of validRows) {
				const res = await backendPostWithoutRefresh("/api/generic-tag/", {
					...row,
					group_id: props.groupId,
					parent_id: props.parentId,
					group_type: props.groupType,
				});
				props.handleAddSuccess(res?.data?.generic_tag);
			}

			setVisible(false); // Close modal only after all submissions are done
			resetForm();       // Reset form after successful submission
		} catch (error) {
			console.error("Error submitting tags:", error);
		} finally {
			setInFlight(false);
		}
	};

	return (
		<GenericModal
			visible={visible}
			handleCancelModal={() => {
				setVisible(false);
				resetForm(); // Reset form on modal close
			}}
			showModal={() => {
				setVisible(true);
				resetForm(); // Reset form on modal open
			}}
			size="md"
			button={
				<Button
					sx={{ fontSize: 14, textTransform: "none" }}
					disableElevation
					size="small"
					variant={props.buttonVariant || "outlined"}
					color={props.buttonColor || "secondary"}
				>
					{props.buttonLabel || "Add Tag"}
				</Button>
			}
			modalTitle="Add New Tag"
			modalBody={
				<Stack direction="column" spacing={2}>
					{rows.map((row, index) => (
						<Stack direction="row" spacing={1} key={index}>
							<TextField
								fullWidth
								label="Name"
								placeholder="Tag Name"
								value={row.name}
								onChange={(e) => handleInputChange(index, "name", e.target.value)}
								onPaste={(e) => handlePaste(e, index)}
								required
							/>
							<TextField
								fullWidth
								label="Description"
								placeholder="Tag Description"
								value={row.description}
								onChange={(e) => handleInputChange(index, "description", e.target.value)}
							/>
							{rows.length > 1 && (
								<IconButton onClick={() => removeRow(index)} color="error">
									<Delete fontSize="large" />
								</IconButton>
							)}
						</Stack>
					))}

					<Button
						sx={{ fontSize: 14, textTransform: "none" }}
						startIcon={<Add />}
						variant="outlined"
						fullWidth
						onClick={addRow}
					>
						Add Row
					</Button>

					<LoadingButton
						sx={{ fontSize: 14, textTransform: "none" }}
						disableElevation
						fullWidth
						size="small"
						variant="contained"
						color="primary"
						onClick={onFormSubmit}
						disabled={inFlight}
						loading={inFlight}
					>
						{inFlight ? <CircularProgress size={20} /> : "Submit"}
					</LoadingButton>
				</Stack>
			}
		/>
	);
};

export default AddCustomTagButton;
